import React, { useState, useMemo } from "react";

const COLUMNS = [
  { id: "todo", title: "To Do", dotColor: "bg-slate-400" },
  { id: "in_progress", title: "In Progress", dotColor: "bg-blue-500" },
  { id: "in_review", title: "In Review", dotColor: "bg-amber-500" },
  { id: "done", title: "Done", dotColor: "bg-emerald-500" },
];

const INITIAL_TASKS = [
  {
    id: "TASK-101",
    title: "Implement OAuth2 Session Handling",
    status: "in_progress",
    priority: "High",
    dept: "IT",
    assignee: "Samkith Jain",
    dueDate: "28 Aug",
    blocks: [
      { id: "b1", type: "text", content: "Ensure JWT persistence and secure cookie configuration across system user sessions." },
      {
        id: "b2",
        type: "checklist",
        items: [
          { id: "c1", text: "Configure HTTPS-only SameSite cookies", done: true },
          { id: "c2", text: "Setup refresh token rotation endpoint", done: false },
        ],
      },
      {
        id: "b3",
        type: "table",
        headers: ["Endpoint", "Auth Required", "Rate Limit"],
        rows: [
          ["/api/auth/login", "No", "5 req/min"],
          ["/api/auth/refresh", "Yes", "20 req/min"],
        ],
      },
    ],
  },
  {
    id: "TASK-102",
    title: "Department Asset & Audit Review",
    status: "todo",
    priority: "Medium",
    dept: "HR",
    assignee: "Sarah Connor",
    dueDate: "30 Aug",
    blocks: [
      { id: "b1", type: "text", content: "Quarterly reconciliation of active laptops and security badges." },
      {
        id: "b2",
        type: "todolist",
        items: [
          { id: "t1", text: "Export active hardware serial numbers" },
          { id: "t2", text: "Cross-check with manager sign-offs" },
        ],
      },
    ],
  },
];

const PRIORITY_BADGES = {
  Low: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700",
  Medium: "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 border-amber-200/80 dark:border-amber-800/50",
  High: "bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300 border-orange-200/80 dark:border-orange-800/50",
  Urgent: "bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300 border-rose-200/80 dark:border-rose-800/50",
};

export default function TaskKanbanBoard({ theme = "light", employees = [], onNotify }) {
  const [tasks, setTasks] = useState(INITIAL_TASKS);
  const [activeDept, setActiveDept] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Drag and drop states
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [activeDropColumn, setActiveDropColumn] = useState(null);
  const [dropPositionIndex, setDropPositionIndex] = useState(null);

  // New task form state
  const [newTask, setNewTask] = useState({
    title: "",
    status: "todo",
    priority: "Medium",
    dept: "IT",
    assignee: "",
    dueDate: "30 Aug",
    blocks: [{ id: "b-init", type: "text", content: "" }],
  });

  // Filter tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      const matchesDept = activeDept === "All" || t.dept === activeDept;
      const matchesPriority = priorityFilter === "All" || t.priority === priorityFilter;
      const matchesSearch =
        t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.assignee.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesDept && matchesPriority && matchesSearch;
    });
  }, [tasks, activeDept, priorityFilter, searchQuery]);

  // Block management helpers
  const addBlock = (setter, type) => {
    const id = `b-${Date.now()}`;
    let newBlock;
    if (type === "text") {
      newBlock = { id, type: "text", content: "" };
    } else if (type === "checklist") {
      newBlock = { id, type: "checklist", items: [{ id: `c-${Date.now()}`, text: "", done: false }] };
    } else if (type === "todolist") {
      newBlock = { id, type: "todolist", items: [{ id: `t-${Date.now()}`, text: "" }] };
    } else if (type === "table") {
      newBlock = {
        id,
        type: "table",
        headers: ["Column 1", "Column 2"],
        rows: [["Value 1", "Value 2"]],
      };
    }
    setter((prev) => ({ ...prev, blocks: [...prev.blocks, newBlock] }));
  };

  const updateBlock = (setter, blockId, updatedData) => {
    setter((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) => (b.id === blockId ? { ...b, ...updatedData } : b)),
    }));
  };

  const removeBlock = (setter, blockId) => {
    setter((prev) => ({
      ...prev,
      blocks: prev.blocks.filter((b) => b.id !== blockId),
    }));
  };

  // Drag and drop handlers
  const handleDragStart = (e, taskId) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOverColumn = (e, colId) => {
    e.preventDefault();
    if (activeDropColumn !== colId) setActiveDropColumn(colId);
  };

  const handleDragOverCard = (e, colId, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const isBelowMiddle = e.clientY - rect.top > rect.height / 2;
    setActiveDropColumn(colId);
    setDropPositionIndex(isBelowMiddle ? targetIndex + 1 : targetIndex);
  };

  const handleDrop = (e, targetColId) => {
    e.preventDefault();
    if (!draggedTaskId) return;

    const currentTask = tasks.find((t) => t.id === draggedTaskId);
    if (!currentTask) return;

    const remainingTasks = tasks.filter((t) => t.id !== draggedTaskId);
    const colTasks = remainingTasks.filter((t) => t.status === targetColId);
    const nonColTasks = remainingTasks.filter((t) => t.status !== targetColId);

    const updatedTask = { ...currentTask, status: targetColId };
    const insertIndex =
      dropPositionIndex !== null && activeDropColumn === targetColId
        ? Math.min(dropPositionIndex, colTasks.length)
        : colTasks.length;

    colTasks.splice(insertIndex, 0, updatedTask);
    setTasks([...nonColTasks, ...colTasks]);

    setDraggedTaskId(null);
    setActiveDropColumn(null);
    setDropPositionIndex(null);

    if (currentTask.status !== targetColId && onNotify) {
      const targetTitle = COLUMNS.find((c) => c.id === targetColId)?.title;
      onNotify(`Moved #${currentTask.id} to "${targetTitle}"`, "success");
    }
  };

  const handleToggleCardChecklist = (taskId, blockId, itemId) => {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id !== taskId) return t;
        return {
          ...t,
          blocks: t.blocks.map((b) => {
            if (b.id !== blockId || b.type !== "checklist") return b;
            return {
              ...b,
              items: b.items.map((it) => (it.id === itemId ? { ...it, done: !it.done } : it)),
            };
          }),
        };
      })
    );
  };

  const handleCreateTask = (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) return;

    const created = {
      ...newTask,
      id: `TASK-${Math.floor(100 + Math.random() * 900)}`,
      assignee: newTask.assignee || "Samkith Jain",
    };

    setTasks([created, ...tasks]);
    setIsModalOpen(false);
    if (onNotify) onNotify(`Created issue #${created.id}`, "success");

    setNewTask({
      title: "",
      status: "todo",
      priority: "Medium",
      dept: "IT",
      assignee: "",
      dueDate: "30 Aug",
      blocks: [{ id: "b-init", type: "text", content: "" }],
    });
  };

  // Render Notion-like description blocks in builder/modal
  const renderBlockEditor = (block, stateSetter, stateObj) => {
    return (
      <div
        key={block.id}
        className="group relative p-3 rounded-lg border border-slate-200 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-800/40 space-y-2"
      >
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-mono uppercase font-bold tracking-wider text-slate-400 dark:text-slate-500">
            {block.type} Block
          </span>
          <button
            type="button"
            onClick={() => removeBlock(stateSetter, block.id)}
            className="text-slate-400 hover:text-red-500 text-xs cursor-pointer"
          >
            <i className="ti ti-trash" />
          </button>
        </div>

        {/* Text Area */}
        {block.type === "text" && (
          <textarea
            rows={2}
            placeholder="Type text, context, or notes..."
            value={block.content || ""}
            onChange={(e) => updateBlock(stateSetter, block.id, { content: e.target.value })}
            className="w-full p-2 text-xs rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:outline-none"
          />
        )}

        {/* Interactive Checklist */}
        {block.type === "checklist" && (
          <div className="space-y-1.5">
            {block.items.map((item, idx) => (
              <div key={item.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={item.done}
                  onChange={(e) => {
                    const newItems = [...block.items];
                    newItems[idx].done = e.target.checked;
                    updateBlock(stateSetter, block.id, { items: newItems });
                  }}
                  className="rounded text-blue-600 focus:ring-0 cursor-pointer"
                />
                <input
                  type="text"
                  placeholder="Checklist step..."
                  value={item.text}
                  onChange={(e) => {
                    const newItems = [...block.items];
                    newItems[idx].text = e.target.value;
                    updateBlock(stateSetter, block.id, { items: newItems });
                  }}
                  className="flex-1 h-7 px-2 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded focus:outline-none"
                />
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newItems = [...block.items, { id: `c-${Date.now()}`, text: "", done: false }];
                updateBlock(stateSetter, block.id, { items: newItems });
              }}
              className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
            >
              + Add step
            </button>
          </div>
        )}

        {/* Bullet To-Do List */}
        {block.type === "todolist" && (
          <div className="space-y-1.5">
            {block.items.map((item, idx) => (
              <div key={item.id} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500" />
                <input
                  type="text"
                  placeholder="To-do action item..."
                  value={item.text}
                  onChange={(e) => {
                    const newItems = [...block.items];
                    newItems[idx].text = e.target.value;
                    updateBlock(stateSetter, block.id, { items: newItems });
                  }}
                  className="flex-1 h-7 px-2 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded focus:outline-none"
                />
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newItems = [...block.items, { id: `t-${Date.now()}`, text: "" }];
                updateBlock(stateSetter, block.id, { items: newItems });
              }}
              className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
            >
              + Add bullet point
            </button>
          </div>
        )}

        {/* Dynamic Table */}
        {block.type === "table" && (
          <div className="space-y-2 overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr>
                  {block.headers.map((h, hIdx) => (
                    <th key={hIdx} className="p-1">
                      <input
                        type="text"
                        value={h}
                        onChange={(e) => {
                          const newHeaders = [...block.headers];
                          newHeaders[hIdx] = e.target.value;
                          updateBlock(stateSetter, block.id, { headers: newHeaders });
                        }}
                        className="w-full font-bold p-1 bg-slate-100 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 focus:outline-none"
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, rIdx) => (
                  <tr key={rIdx}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} className="p-1">
                        <input
                          type="text"
                          value={cell}
                          onChange={(e) => {
                            const newRows = [...block.rows];
                            newRows[rIdx][cIdx] = e.target.value;
                            updateBlock(stateSetter, block.id, { rows: newRows });
                          }}
                          className="w-full p-1 bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 focus:outline-none"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-2 text-[11px]">
              <button
                type="button"
                onClick={() => {
                  const newRows = [...block.rows, new Array(block.headers.length).fill("")];
                  updateBlock(stateSetter, block.id, { rows: newRows });
                }}
                className="text-blue-600 dark:text-blue-400 font-semibold hover:underline cursor-pointer"
              >
                + Add row
              </button>
              <button
                type="button"
                onClick={() => {
                  const newHeaders = [...block.headers, `Column ${block.headers.length + 1}`];
                  const newRows = block.rows.map((r) => [...r, ""]);
                  updateBlock(stateSetter, block.id, { headers: newHeaders, rows: newRows });
                }}
                className="text-blue-600 dark:text-blue-400 font-semibold hover:underline cursor-pointer"
              >
                + Add column
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col gap-5 font-sans">
      {/* Search & Actions Header */}
      <div className="p-4 rounded-xl flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4 bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex flex-wrap items-center gap-3 flex-1">
          <div className="relative min-w-[240px] flex-1 max-w-sm">
            <i className="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              placeholder="Search sprint issues, descriptions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 pl-9 pr-3 text-xs rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Department Tabs */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800/60 p-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60">
            {["All", "IT", "HR"].map((dept) => (
              <button
                key={dept}
                onClick={() => setActiveDept(dept)}
                className={`px-3 py-1 rounded-md text-xs font-semibold cursor-pointer transition-all ${
                  activeDept === dept
                    ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-xs"
                    : "text-slate-600 dark:text-slate-400"
                }`}
              >
                {dept}
              </button>
            ))}
          </div>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="h-9 px-3 text-xs rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 font-medium focus:outline-none cursor-pointer"
          >
            <option value="All">All Priorities</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Urgent">Urgent</option>
          </select>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center justify-center gap-2 h-9 px-4 rounded-lg text-xs font-semibold text-white bg-blue-600 hover:bg-blue-500 shadow-md cursor-pointer transition"
        >
          <i className="ti ti-plus text-sm" />
          <span>New Structured Issue</span>
        </button>
      </div>

      {/* Kanban Board Columns Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
        {COLUMNS.map((col) => {
          const colTasks = filteredTasks.filter((t) => t.status === col.id);
          const isColumnHovered = activeDropColumn === col.id;

          return (
            <div
              key={col.id}
              onDragOver={(e) => handleDragOverColumn(e, col.id)}
              onDrop={(e) => handleDrop(e, col.id)}
              className={`flex flex-col gap-3 p-3.5 rounded-xl border min-h-[580px] transition-colors ${
                isColumnHovered
                  ? "bg-blue-50/40 dark:bg-blue-950/10 border-blue-400/80"
                  : "bg-slate-50/70 dark:bg-[#0E131F] border-slate-200/80 dark:border-slate-800"
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${col.dotColor}`} />
                  <span className="text-xs font-bold uppercase tracking-wider font-mono text-slate-800 dark:text-slate-200">
                    {col.title}
                  </span>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-md font-bold font-mono bg-slate-200/70 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                  {colTasks.length}
                </span>
              </div>

              {/* Stack */}
              <div className="flex flex-col gap-2.5 flex-1">
                {colTasks.map((task, index) => {
                  const isBeingDragged = draggedTaskId === task.id;
                  const showDropAbove = isColumnHovered && dropPositionIndex === index && draggedTaskId !== task.id;
                  const showDropBelow =
                    isColumnHovered &&
                    dropPositionIndex === index + 1 &&
                    index === colTasks.length - 1 &&
                    draggedTaskId !== task.id;

                  // Extract checklist progress
                  const checkBlocks = task.blocks?.filter((b) => b.type === "checklist") || [];
                  const totalChecks = checkBlocks.reduce((acc, b) => acc + b.items.length, 0);
                  const doneChecks = checkBlocks.reduce(
                    (acc, b) => acc + b.items.filter((i) => i.done).length,
                    0
                  );

                  return (
                    <React.Fragment key={task.id}>
                      {showDropAbove && <div className="h-1 bg-blue-500 rounded-full animate-pulse" />}

                      <div
                        draggable
                        onDragStart={(e) => handleDragStart(e, task.id)}
                        onDragOver={(e) => handleDragOverCard(e, col.id, index)}
                        onClick={() => setSelectedTask(task)}
                        className={`group relative p-3.5 rounded-lg bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 shadow-xs cursor-grab active:cursor-grabbing hover:shadow-md transition-all ${
                          isBeingDragged ? "opacity-25 border-dashed border-blue-500" : "opacity-100"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="text-[11px] font-bold font-mono text-blue-600 dark:text-blue-400">
                            #{task.id}
                          </span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${PRIORITY_BADGES[task.priority]}`}>
                            {task.priority}
                          </span>
                        </div>

                        <h4 className="text-xs font-semibold text-slate-900 dark:text-slate-100">{task.title}</h4>

                        {/* Notion-Style Block Previews inside Card */}
                        <div className="mt-2 space-y-2">
                          {task.blocks?.map((block) => {
                            if (block.type === "text" && block.content) {
                              return (
                                <p key={block.id} className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2">
                                  {block.content}
                                </p>
                              );
                            }
                            if (block.type === "checklist" && block.items?.length > 0) {
                              return (
                                <div key={block.id} className="space-y-1 pt-1">
                                  {block.items.slice(0, 2).map((item) => (
                                    <label
                                      key={item.id}
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-400 cursor-pointer"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={item.done}
                                        onChange={() => handleToggleCardChecklist(task.id, block.id, item.id)}
                                        className="w-3 h-3 rounded text-blue-600 cursor-pointer"
                                      />
                                      <span className={item.done ? "line-through opacity-50" : ""}>{item.text}</span>
                                    </label>
                                  ))}
                                </div>
                              );
                            }
                            if (block.type === "todolist" && block.items?.length > 0) {
                              return (
                                <div key={block.id} className="space-y-1">
                                  {block.items.slice(0, 2).map((item) => (
                                    <div key={item.id} className="flex items-center gap-1.5 text-[11px] text-slate-500">
                                      <span className="w-1 h-1 rounded-full bg-slate-400" />
                                      <span className="truncate">{item.text}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            if (block.type === "table" && block.rows?.length > 0) {
                              return (
                                <div
                                  key={block.id}
                                  className="flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-2 py-0.5 rounded border border-blue-200 dark:border-blue-900 w-fit"
                                >
                                  <i className="ti ti-table" />
                                  <span>{block.rows.length} row table</span>
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>

                        {/* Progress meter if checklist exists */}
                        {totalChecks > 0 && (
                          <div className="mt-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                            <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                              <span>Checklist</span>
                              <span>{doneChecks}/{totalChecks}</span>
                            </div>
                            <div className="w-full h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-emerald-500"
                                style={{ width: `${(doneChecks / totalChecks) * 100}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100 dark:border-slate-800 text-[11px]">
                          <div className="flex items-center gap-1.5">
                            <div className="w-5 h-5 rounded-full bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-[10px] uppercase">
                              {task.assignee.charAt(0)}
                            </div>
                            <span className="text-slate-600 dark:text-slate-400 font-medium">{task.assignee.split(" ")[0]}</span>
                          </div>
                          <span className="font-mono text-[10px] text-slate-400">{task.dueDate}</span>
                        </div>
                      </div>

                      {showDropBelow && <div className="h-1 bg-blue-500 rounded-full animate-pulse" />}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Notion-Like Task Creator Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 rounded-2xl bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">Create New Structured Task</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-200 cursor-pointer">
                <i className="ti ti-x text-sm" />
              </button>
            </div>

            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-1 text-slate-700 dark:text-slate-300">Task Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Design Token Migration"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  className="w-full h-9 px-3 text-xs rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-slate-700 dark:text-slate-300">Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                    className="w-full h-9 px-2.5 text-xs rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-slate-700 dark:text-slate-300">Assignee</label>
                  <select
                    value={newTask.assignee}
                    onChange={(e) => setNewTask({ ...newTask, assignee: e.target.value })}
                    className="w-full h-9 px-2.5 text-xs rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                  >
                    <option value="">Select Employee...</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.name}>{emp.name} ({emp.department})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Notion-like Block Writing Section */}
              <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Description & Notion-Like Blocks
                  </label>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => addBlock(setNewTask, "text")}
                      className="px-2 py-1 text-[11px] rounded bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 font-medium cursor-pointer"
                    >
                      + Text
                    </button>
                    <button
                      type="button"
                      onClick={() => addBlock(setNewTask, "checklist")}
                      className="px-2 py-1 text-[11px] rounded bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 font-medium cursor-pointer"
                    >
                      + Checklist
                    </button>
                    <button
                      type="button"
                      onClick={() => addBlock(setNewTask, "todolist")}
                      className="px-2 py-1 text-[11px] rounded bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 font-medium cursor-pointer"
                    >
                      + To-Do List
                    </button>
                    <button
                      type="button"
                      onClick={() => addBlock(setNewTask, "table")}
                      className="px-2 py-1 text-[11px] rounded bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 font-medium cursor-pointer"
                    >
                      + Table
                    </button>
                  </div>
                </div>

                <div className="space-y-2 mt-2">
                  {newTask.blocks.map((block) => renderBlockEditor(block, setNewTask, newTask))}
                </div>
              </div>

              <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 h-9 rounded-lg text-xs font-medium bg-slate-100 dark:bg-slate-800 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 h-9 rounded-lg text-xs font-semibold text-white bg-blue-600 hover:bg-blue-500 shadow-sm cursor-pointer"
                >
                  Save Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Inspection & Edit Drawer Modal */}
      {selectedTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 rounded-2xl bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <div>
                <span className="text-xs font-mono font-bold text-blue-600 dark:text-blue-400">
                  #{selectedTask.id}
                </span>
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">{selectedTask.title}</h3>
              </div>
              <button onClick={() => setSelectedTask(null)} className="text-slate-400 hover:text-slate-200 cursor-pointer">
                <i className="ti ti-x text-sm" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex gap-4 text-xs">
                <div>
                  <span className="text-slate-400 block">Status</span>
                  <span className="font-semibold uppercase font-mono">{selectedTask.status}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Priority</span>
                  <span className="font-semibold">{selectedTask.priority}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Assignee</span>
                  <span className="font-semibold">{selectedTask.assignee}</span>
                </div>
              </div>

              <div className="space-y-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Structured Description</h4>
                {selectedTask.blocks?.map((block) => (
                  <div key={block.id} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    {block.type === "text" && <p className="text-xs text-slate-700 dark:text-slate-300">{block.content}</p>}
                    {block.type === "checklist" && (
                      <div className="space-y-1.5">
                        {block.items.map((it) => (
                          <label key={it.id} className="flex items-center gap-2 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={it.done}
                              onChange={() => handleToggleCardChecklist(selectedTask.id, block.id, it.id)}
                              className="rounded text-blue-600"
                            />
                            <span className={it.done ? "line-through opacity-50" : ""}>{it.text}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    {block.type === "todolist" && (
                      <ul className="list-disc pl-4 text-xs space-y-1 text-slate-700 dark:text-slate-300">
                        {block.items.map((it) => (
                          <li key={it.id}>{it.text}</li>
                        ))}
                      </ul>
                    )}
                    {block.type === "table" && (
                      <table className="w-full text-xs text-left border-collapse border border-slate-200 dark:border-slate-700">
                        <thead>
                          <tr className="bg-slate-100 dark:bg-slate-700">
                            {block.headers.map((h, i) => (
                              <th key={i} className="p-2 border border-slate-200 dark:border-slate-700">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {block.rows.map((r, ri) => (
                            <tr key={ri}>
                              {r.map((c, ci) => (
                                <td key={ci} className="p-2 border border-slate-200 dark:border-slate-700">{c}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end pt-3 border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={() => setSelectedTask(null)}
                className="px-4 h-9 rounded-lg text-xs font-semibold bg-blue-600 text-white cursor-pointer"
              >
                Close Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}